import { AdminSettings, AdminSettingsRepository, IAdminSettingsRepository } from '../repository/admin-settings.repository';

export interface IAdminSettingsService {
  getSettings(): Promise<AdminSettings>;
  updateSettings(settings: Partial<AdminSettings>): Promise<void>;
  resetToDefaults(): Promise<void>;
}

/**
 * Admin Settings Service
 * Handles business logic for admin settings management
 */
export class AdminSettingsService implements IAdminSettingsService {
  private adminSettingsRepository: IAdminSettingsRepository;

  constructor(adminSettingsRepository: IAdminSettingsRepository = new AdminSettingsRepository()) {
    this.adminSettingsRepository = adminSettingsRepository;
    console.log('[AdminSettingsService] Constructor - Initialized');
  }

  /**
   * Get current admin settings
   */
  async getSettings(): Promise<AdminSettings> {
    console.log('[AdminSettingsService.getSettings] START');
    try {
      const settings = await this.adminSettingsRepository.getSettings();
      console.log('[AdminSettingsService.getSettings] SUCCESS');
      return settings;
    } catch (error) {
      console.error('[AdminSettingsService.getSettings] ERROR:', error);
      throw new Error('Failed to load admin settings');
    }
  }

  /**
   * Update admin settings
   */
  async updateSettings(settingsUpdate: Partial<AdminSettings>): Promise<void> {
    console.log('[AdminSettingsService.updateSettings] START - Update:', settingsUpdate);
    try {
      // Validate settings before updating
      this.validateSettings(settingsUpdate);

      await this.adminSettingsRepository.updateSettings(settingsUpdate);
      console.log('[AdminSettingsService.updateSettings] SUCCESS');
    } catch (error) {
      console.error('[AdminSettingsService.updateSettings] ERROR:', error);
      throw new Error('Failed to update admin settings');
    }
  }

  /**
   * Reset settings to defaults
   */
  async resetToDefaults(): Promise<void> {
    console.log('[AdminSettingsService.resetToDefaults] START');
    try {
      const defaultSettings = this.getDefaultSettings();
      await this.adminSettingsRepository.updateSettings(defaultSettings);
      console.log('[AdminSettingsService.resetToDefaults] SUCCESS');
    } catch (error) {
      console.error('[AdminSettingsService.resetToDefaults] ERROR:', error);
      throw new Error('Failed to reset settings to defaults');
    }
  }

  /**
   * Validate settings update
   */
  private validateSettings(settings: Partial<AdminSettings>): void {
    // Validate email format
    if (settings.emailFrom && !this.isValidEmail(settings.emailFrom)) {
      throw new Error('Invalid email format for emailFrom');
    }

    // Validate URL format
    if (settings.siteUrl && !this.isValidUrl(settings.siteUrl)) {
      throw new Error('Invalid URL format for siteUrl');
    }

    // Validate numeric values
    if (settings.maxFileSize && (settings.maxFileSize <= 0 || settings.maxFileSize > 200)) {
      throw new Error('Max file size must be between 1 and 200 MB');
    }

    if (settings.sessionTimeout && (settings.sessionTimeout <= 0 || settings.sessionTimeout > 1440)) {
      throw new Error('Session timeout must be between 1 and 1440 minutes');
    }

    if (settings.maxLoginAttempts && (settings.maxLoginAttempts <= 0 || settings.maxLoginAttempts > 20)) {
      throw new Error('Max login attempts must be between 1 and 20');
    }

    // Validate strings
    if (settings.siteName && settings.siteName.trim().length === 0) {
      throw new Error('Site name cannot be empty');
    }
  }

  /**
   * Validate email format
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate URL format
   */
  private isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get default settings (same as repository but available for reset)
   */
  private getDefaultSettings(): AdminSettings {
    return {
      siteName: "Dance Realm X",
      siteDescription: "Professional dance education platform",
      siteUrl: "https://dancerealmx.com",
      emailFrom: "noreply@dancerealmx.com",
      registrationEnabled: true,
      emailVerificationRequired: true,
      maintenanceMode: false,
      currency: "USD",
      timezone: "America/New_York",
      maxFileSize: 10,
      sessionTimeout: 30,
      maxLoginAttempts: 5,
    };
  }
}